uniform float uTime;
uniform float uFadeOut;
uniform float uSeed;
attribute float aSize;
attribute float aOffset;
attribute vec3 aRandom;

varying float vAlpha;
varying float vHeat; // Замість vLife використовуємо "Heat"

// Швидкий 3D Simplex Noise для WebGL
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    vec3 i  = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy * 2.0;
    vec3 x3 = x0 - D.yyy;
    i = mod289(i);
    vec4 p = permute(permute(permute(i.z + vec4(0.0, i1.z, i2.z, 1.0)) + i.y + vec4(0.0, i1.y, i2.y, 1.0)) + i.x + vec4(0.0, i1.x, i2.x, 1.0));
    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);
    vec4 x = x_ * ns.x + ns.y;
    vec4 y = y_ * ns.x + ns.y;
    vec4 h = 1.0 - abs(x) - abs(y);
    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
    p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}

void main() {
    float t = mod(uTime * 0.4 + aOffset, 1.0);
    vHeat = 1.0 - t; // Тепло падає знизу вгору

    vec3 pos = position;
    
    // 1. Принцип Буйяності (Buoyancy): частки прискорюються вгору
    pos.y += pow(t, 1.5) * 1.05;

    // 2. Турбулентна Адвекція (Noise-based displacement)
    // Використовуємо шум для зміщення XZ, що створює ефект "язиків" полум'я
    float noiseScale = 2.0;
    float noiseFreq = 1.5;
    float noiseExp = snoise(vec3(pos.x * noiseFreq, pos.y * noiseFreq - (uTime + uSeed), pos.z * noiseFreq));

    // Чим вище частка (чим менше vHeat), тим сильніша турбулентність
    float displacement = (1.0 - vHeat) * 0.15;
    pos.x += noiseExp * displacement;
    pos.z += snoise(vec3(pos.z, pos.y, uTime + uSeed)) * displacement;

    // 3. Форма конуса
    float radius = 0.040 * vHeat; // Звуження доверху
    pos.x += aRandom.x * radius;
    pos.z += aRandom.z * radius;

    // 4. Альфа-маскування (прозорість)
    vAlpha = smoothstep(0.0, 0.2, vHeat) * vHeat * uFadeOut;

    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
    
    // Розмір частки: вогонь має бути дрібним і густим в основі
    float sizeScale = mix(0.2, 1.2, vHeat);
    gl_PointSize = aSize * sizeScale * (400.0 / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
}